import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import { PlusIcon, UploadSimpleIcon } from "@phosphor-icons/react";

import { CreatorsPageWrapperStyles, TableWrapperStyles } from "./styled";

import PillButtons from "components/Buttons/PillButtons";
import CommonTable from "components/Tables/CommonTable";

import {
  CardFlexWrapperStyles,
  CardWrapperStyles
} from "pages/TalentUploadRequest/styled";

import columns from "pages/Creators/column";

import { useCreatorsSlice } from "pages/Creators/slice";
import { selectCreators } from "pages/Creators/slice/selector";
import { getLevelCardsData, searchFieldOptions } from "./data";
import { useDropdownSlice } from "store/dropdownOptions/slice/index";
import {
  selectCities,
  selectCompanies,
  selectStates
} from "store/dropdownOptions/slice/selector";
import Icon from "components/Icon/Icon";

import {
  CaretDownIcon,
  CalendarBlankIcon,
  MagnifyingGlassIcon,
  XIcon,
  XCircleIcon
} from "@phosphor-icons/react";
import CustomDateModal from "components/CustomDateModal/index";
import { format } from "date-fns";
import Input from "components/Input/index";
import LoadingSpinner from "components/PageLoader/Spinner";
import Select from "components/Select/index";
import { reorderDropdown } from "utils/helper";
import Button from "components/Buttons/index";

const CreatorsPage = () => {
  const dispatch = useDispatch();
  const { actions } = useCreatorsSlice();
  const { actions: dropdownActions } = useDropdownSlice();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ===== URL-based filters =====
  const urlState = useMemo(
    () => ({
      page: Number(searchParams.get("page")) || 1,
      limit: Number(searchParams.get("limit")) || 10,
      state: searchParams.get("state") || "",
      city: searchParams.get("city") || "",
      company: searchParams.get("company") || "",
      managed_by: searchParams.get("managed_by") || "",
      search: searchParams.get("search") || "",
      search_field: searchParams.get("search_field") || "ig_handle"
    }),
    [searchParams]
  );

  const [showDateModal, setShowDateModal] = useState(false);
  const [customDateRange, setCustomDateRange] = useState([null, null]);
  const [searchTerm, setSearchTerm] = useState(urlState.search || "");
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  const isInitialMount = useRef(true);
  const debounceRef = useRef(null);
  const searchFieldRef = useRef(urlState.search_field); // always latest search_field

  const {
    initialLoading,
    loading,
    levels,
    states,
    cities,
    companies,
    managed_by,
    total_creators,
    creators = [],
    pagination
  } = useSelector(selectCreators);

  const { data: chooseStates } = useSelector(selectStates);
  const { data: chooseCities } = useSelector(selectCities);
  const { data: chooseCompanies } = useSelector(selectCompanies);

  const syncURL = (updates = {}) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined || value === "") params.delete(key);
      else params.set(key, value);
    });
    setSearchParams(params);
  };

  // ===== Keep searchFieldRef in sync =====
  useEffect(() => {
    searchFieldRef.current = urlState.search_field; // always latest
  }, [urlState.search_field]);

  // ===== Debounce searchTerm =====
  useEffect(() => {
    debounceRef.current = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(debounceRef.current);
  }, [searchTerm]);

  // ===== Update URL with debounced search =====
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    syncURL({
      search: debouncedSearch,
      search_field: searchFieldRef.current, // always latest, no stale closure
      page: 1
    });
  }, [debouncedSearch]);

  // ===== Fetch creators when filters or date changes =====
  useEffect(() => {
    const { search_field, ...filters } = urlState;

    dispatch(
      actions.fetchCreators({
        ...filters,
        from_date: customDateRange[0]
          ? format(customDateRange[0], "yyyy-MM-dd")
          : undefined,
        to_date: customDateRange[1]
          ? format(customDateRange[1], "yyyy-MM-dd")
          : undefined,
        period: customDateRange[0] ? "custom_range" : undefined,
        search_field
      })
    );
  }, [
    urlState.page,
    urlState.limit,
    urlState.state,
    urlState.city,
    urlState.company,
    urlState.managed_by,
    urlState.search,
    customDateRange,
    ...(urlState.search ? [urlState.search_field] : [])
  ]);

  // ===== Fetch dropdown data =====
  useEffect(() => {
    dispatch(dropdownActions.getStatesRequest());
    dispatch(dropdownActions.getCitiesRequest());
    dispatch(dropdownActions.getCompaniesRequest());
  }, []);

  const handlePageChange = (newPage) => syncURL({ page: newPage });

  const levelCounts = useMemo(() => {
    const map = {};
    levels?.forEach((item) => (map[item.level] = item.count));
    return map;
  }, [levels]);

  const tableColumns = useMemo(() => columns(), []);

  const handleCustomDateApply = (range) => {
    if (!range || !range[0] || !range[1]) return;
    setCustomDateRange(range);
    setShowDateModal(false);
  };

  // ===== Helper to build menu items with counts =====
  const buildMenuItems = (
    options = [],
    facetData = [],
    totalCount = 0,
    keyField = "name",
    facetKey = "state_name"
  ) => {
    const countMap = facetData?.reduce((acc, item) => {
      const key = item[facetKey]?.toLowerCase();
      if (key) acc[key] = item.total_count;
      return acc;
    }, {});

    const menuItems = [];

    options?.forEach((opt) => {
      const value = opt[keyField];
      if (!value) return;
      const key = value.toLowerCase();
      const count = countMap[key] || 0;
      if (count === 0) return;
      menuItems.push({ label: value, value, count });
    });

    return menuItems;
  };

  // ===== State Menu Items =====
  const stateMenuItems = useMemo(
    () =>
      buildMenuItems(
        chooseStates,
        states,
        total_creators,
        "name",
        "state_name"
      ),
    [chooseStates, states, total_creators]
  );

  // ===== City Menu Items =====
  const cityMenuItems = useMemo(
    () =>
      buildMenuItems(chooseCities, cities, total_creators, "name", "city_name"),
    [chooseCities, cities, total_creators]
  );

  // ===== Company Menu Items =====
  const companyMenuItems = useMemo(
    () =>
      buildMenuItems(
        chooseCompanies,
        companies,
        total_creators,
        "company_name",
        "companies.company_name"
      ),
    [chooseCompanies, companies, total_creators]
  );

  // ===== Managed By Menu Items =====
  const managedByMenuItems = useMemo(() => {
    const apiData = managed_by || [];
    const map = {};
    apiData.forEach((item) => {
      const key = item.managed_by || "";
      map[key] = item.total_count;
    });

    const menuItems = [];
    ["personal_managed", "self_managed", "talent_managed"].forEach((value) => {
      const count = map[value] || 0;
      if (count) {
        menuItems.push({
          label:
            value === "personal_managed"
              ? "Personal Managed"
              : value === "self_managed"
                ? "Self Managed"
                : "Talent Managed",
          value,
          count
        });
      }
    });

    return menuItems;
  }, [managed_by, total_creators]);

  const selectedOption = searchFieldOptions.find(
    (opt) => opt.value === urlState?.search_field
  );

  const dynamicPlaceholder = selectedOption
    ? `Search by ${selectedOption.label}`
    : "Search";

  return (
    <>
      <CreatorsPageWrapperStyles>
        <div className="header-wrapper">
          <h1 className="title">Creators</h1>

          <div className="header-side-content">
            {/* <Button
              leftIcon={<Icon component={PlusIcon} />}
              variant="secondary"
              onClick={() => navigate("/creators/add")}
            >
              Add Creator
            </Button>
            <Button
              leftIcon={<Icon component={UploadSimpleIcon} />}
              onClick={() => navigate("/creators/bulk-import")}
            >
              Bulk Import
            </Button> */}
          </div>
        </div>

        <CardFlexWrapperStyles>
          {getLevelCardsData(levelCounts).map((item) => (
            <CardWrapperStyles key={item.title}>
              <div className="card-list">
                <span className="card-title">{item.title}</span>
              </div>
              <p className="card-count">{item.count}</p>
            </CardWrapperStyles>
          ))}
        </CardFlexWrapperStyles>

        <TableWrapperStyles>
          <div className="filter-bar">
            <div className="filter-bar-menus">
              <PillButtons
                placeholderText="State"
                mainLabelText={urlState.state}
                menuItems={reorderDropdown(stateMenuItems, urlState.state)}
                onSelect={(item) => {
                  if (!item.value) {
                    syncURL({ page: 1, state: "", city: "" });
                  } else {
                    syncURL({ page: 1, state: item.value });
                  }
                }}
                size="M"
                customClassName="state-dropdown"
                rightIcon={
                  <Icon
                    component={CaretDownIcon}
                    format="outline"
                    weight="regular"
                  />
                }
                activeRightIcon={
                  <Icon
                    component={XCircleIcon}
                    format="outline"
                    weight="regular"
                  />
                }
                search={true}
              />

              <PillButtons
                placeholderText="City"
                mainLabelText={urlState.city}
                menuItems={reorderDropdown(cityMenuItems, urlState.city)}
                onSelect={(item) => syncURL({ page: 1, city: item.value })}
                size="M"
                customClassName="state-dropdown"
                rightIcon={
                  <Icon
                    component={CaretDownIcon}
                    format="outline"
                    weight="regular"
                  />
                }
                activeRightIcon={
                  <Icon
                    component={XCircleIcon}
                    format="outline"
                    weight="regular"
                  />
                }
                search={true}
              />

              <PillButtons
                placeholderText="Company"
                mainLabelText={urlState.company}
                menuItems={reorderDropdown(companyMenuItems, urlState.company)}
                onSelect={(item) => syncURL({ page: 1, company: item.value })}
                size="M"
                customClassName="state-dropdown company-dropdown"
                rightIcon={
                  <Icon
                    component={CaretDownIcon}
                    format="outline"
                    weight="regular"
                  />
                }
                activeRightIcon={
                  <Icon
                    component={XCircleIcon}
                    format="outline"
                    weight="regular"
                  />
                }
                search={true}
              />

              <PillButtons
                placeholderText="Managed By"
                mainLabelText={
                  urlState.managed_by
                    ? managedByMenuItems.find(
                        (o) => o.value === urlState.managed_by
                      )?.label
                    : ""
                }
                menuItems={managedByMenuItems}
                onSelect={(item) =>
                  syncURL({ page: 1, managed_by: item.value })
                }
                size="M"
                customClassName="state-dropdown"
                rightIcon={
                  <Icon
                    component={CaretDownIcon}
                    format="outline"
                    weight="regular"
                  />
                }
                activeRightIcon={
                  <Icon
                    component={XCircleIcon}
                    format="outline"
                    weight="regular"
                  />
                }
              />

              <div onClick={() => setShowDateModal(true)}>
                <PillButtons
                  placeholderText="Choose Date"
                  mainLabelText={
                    customDateRange[0] && customDateRange[1]
                      ? `${format(customDateRange[0], "d MMM")} - ${format(customDateRange[1], "d MMM, yyyy")}`
                      : ""
                  }
                  size="M"
                  leftIcon={
                    <Icon
                      component={CalendarBlankIcon}
                      format="outline"
                      weight="regular"
                      size={12}
                    />
                  }
                  activeRightIcon={
                    <Icon
                      component={XCircleIcon}
                      format="outline"
                      weight="regular"
                    />
                  }
                  onClear={() => setCustomDateRange([null, null])}
                />
              </div>
            </div>

            <div className="search-dropdown-styles">
              <Select
                size="S"
                selectWrapperClassName="search-select"
                options={searchFieldOptions}
                value={urlState.search_field}
                onChange={(value) => syncURL({ search_field: value })}
              />
              <Input
                id="searchTable"
                type="text"
                placeholder={dynamicPlaceholder}
                leftIcon={
                  <Icon
                    component={MagnifyingGlassIcon}
                    format="outline"
                    weight="regular"
                  />
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && searchTerm.trim()) {
                    clearTimeout(debounceRef.current); // kill pending debounce
                    setDebouncedSearch(searchTerm); // sync debounce state
                    syncURL({
                      search: searchTerm.trim(),
                      search_field: searchFieldRef.current, // always latest
                      page: 1
                    });
                  }
                }}
                size="S"
                className="search-input"
                styledInput="wrap-text"
                multipleFilter="multiple-filter"
                rightIcon={
                  loading ? (
                    <LoadingSpinner color="#1f1f1f" size={12} />
                  ) : (
                    <Icon
                      component={XIcon}
                      format="outline"
                      weight="regular"
                      onClick={() => {
                        setSearchTerm("");
                        syncURL({ search: "", page: 1 });
                      }}
                    />
                  )
                }
                inputProps={{ readOnly: false }}
              />
            </div>
          </div>

          <CommonTable
            data={creators}
            columns={tableColumns}
            loading={loading}
            pagination={pagination}
            total_count={total_creators}
            initialLoading={initialLoading}
            page={urlState.page}
            limit={pagination?.limit || 10}
            onPageChange={handlePageChange}
            tableClassName="creators-table"
          />
        </TableWrapperStyles>
      </CreatorsPageWrapperStyles>

      {showDateModal && (
        <CustomDateModal
          show={showDateModal}
          showMonth={2}
          initialRange={customDateRange}
          onClose={() => setShowDateModal(false)}
          onApply={handleCustomDateApply}
        />
      )}
    </>
  );
};

export default CreatorsPage;
